// PDF密碼檢測和解鎖服務
import * as pdfjsLib from 'pdfjs-dist';

// 設置PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export interface PasswordProtectedResult {
  isPasswordProtected: boolean;
  needsPassword: boolean;
  error?: string;
}

export interface UnlockResult {
  success: boolean;
  unlockedData?: ArrayBuffer;
  error?: string;
}

class PDFPasswordService {
  
  /**
   * 檢測PDF是否需要密碼
   */
  async checkPasswordProtection(file: File): Promise<PasswordProtectedResult> {
    try {
      console.log('開始檢測PDF密碼保護:', file.name);
      
      const arrayBuffer = await file.arrayBuffer();
      
      // 嘗試加載PDF，不提供密碼
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        // 不提供密碼，如果PDF需要密碼會拋出錯誤
      });
      
      try {
        await loadingTask.promise;
        console.log('PDF不需要密碼');
        return {
          isPasswordProtected: false,
          needsPassword: false
        };
      } catch (error: any) {
        // 檢查是否是因為需要密碼而失敗
        if (error.name === 'PasswordException' || 
            error.message?.includes('password') ||
            error.message?.includes('encrypted')) {
          console.log('PDF需要密碼保護');
          return {
            isPasswordProtected: true,
            needsPassword: true
          };
        } else {
          // 其他錯誤
          console.error('PDF檢測失敗:', error);
          return {
            isPasswordProtected: false,
            needsPassword: false,
            error: error.message || 'PDF檢測失敗'
          };
        }
      }
    } catch (error: any) {
      console.error('PDF密碼檢測失敗:', error);
      return {
        isPasswordProtected: false,
        needsPassword: false,
        error: error.message || 'PDF密碼檢測失敗'
      };
    }
  }
  
  /**
   * 使用密碼解鎖PDF
   */
  async unlockPDF(file: File, password: string): Promise<UnlockResult> {
    try {
      console.log('開始解鎖PDF:', file.name);
      
      const arrayBuffer = await file.arrayBuffer();
      
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        password: password
      });
      
      try {
        await loadingTask.promise;
        console.log('PDF解鎖成功');
        
        // 返回解鎖後的數據
        return {
          success: true,
          unlockedData: arrayBuffer
        };
      } catch (error: any) {
        if (error.name === 'PasswordException' || 
            error.message?.includes('password') ||
            error.message?.includes('incorrect')) {
          console.log('密碼錯誤');
          return {
            success: false,
            error: '密碼錯誤，請重新輸入'
          };
        } else {
          console.error('PDF解鎖失敗:', error);
          return {
            success: false,
            error: error.message || 'PDF解鎖失敗'
          };
        }
      }
    } catch (error: any) {
      console.error('PDF解鎖過程失敗:', error);
      return {
        success: false,
        error: error.message || 'PDF解鎖過程失敗'
      };
    }
  }
  
  /**
   * 從解鎖的PDF提取文本（與無密碼PDF使用相同的提取邏輯）
   */
  async extractTextFromUnlockedPDF(arrayBuffer: ArrayBuffer, password: string): Promise<string> {
    try {
      console.log('開始從解鎖的PDF提取文本');
      
      const loadingTask = pdfjsLib.getDocument({
        data: arrayBuffer,
        password: password
      });
      
      const pdf = await loadingTask.promise;
      let fullText = '';
      
      // 提取前5頁的文本（與無密碼PDF保持一致）
      const pageCount = Math.min(pdf.numPages, 5);
      
      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // 使用與advancedPDFService相同的文本提取邏輯
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ');
        
        fullText += pageText + ' '; // 使用空格分隔，與advancedPDFService保持一致
      }
      
      // 標準化文本格式（與advancedPDFService保持一致）
      const normalizedText = this.normalizeText(fullText.trim());
      
      console.log('PDF文本提取完成，文本長度:', normalizedText.length);
      console.log('提取頁數:', pageCount, '/', pdf.numPages);
      return normalizedText;
    } catch (error: any) {
      console.error('從解鎖PDF提取文本失敗:', error);
      throw new Error(`文本提取失敗: ${error.message}`);
    }
  }
  
  /**
   * 標準化文本格式，確保與advancedPDFService提取的文本格式一致
   */
  private normalizeText(text: string): string {
    return text
      // 移除多餘的空白字符
      .replace(/\s+/g, ' ')
      // 移除開頭和結尾的空白
      .trim();
  }
}

export const pdfPasswordService = new PDFPasswordService();
